import React, { useState, useMemo, useCallback } from "react";
import { connect } from "react-redux";
import Wrp from "../../../hoc/Wrp/Wrp";
import SubformTypeForm from '../../../Containers/LayoutContainer/LayoutForms/SubformTypeForm';
import SectionTypeForm from '../../../Containers/LayoutContainer/LayoutForms/SectionTypeForm';
import GroupEditForm from '../../../Containers/LayoutContainer/LayoutForms/SubformTypeForm/GroupEditForm';
import { isSet } from "../../../Utils/CommonFunctions";
import { setSubFormDataAction, clearSubformValidation } from "../../../store/actions/formBuilderActions";
import { getSectionData, getSubformIndex } from "./utils/subformHelpers";
import { 
    createAddRowHandler, 
    createBulkDeleteHandler, 
    createBulkEditChangeHandler, 
    createBulkEditSaveHandler 
} from "./utils/subformHandlers";
// import { getOutputFieldsElement,isValidJson } from "../MarkupFunctions";

const ScmInputSectionField = ({ attributes, elmtParams, setSubFormDataAction, clearSubformValidation })=>{
    const {convertAndRenderElement} = elmtParams;
    const {loopData,dataTable,dynamicVar,children,childActionButtons} = elmtParams;

    let attribsName = (attributes.hasOwnProperty('name')===true)?attributes.name:'';
    let loopAttribs = (attributes.hasOwnProperty('loop')===true)?attributes.loop:'';
    const {defProps} = elmtParams;
    const {modType,fieldGroups} = defProps;
    
    // Bulk edit state management
    const [bulkEditPopupOpen, setBulkEditPopupOpen] = useState(false);
    const [bulkEditPayload, setBulkEditPayload] = useState({ type: "", value: "" });
    const [bulkEditItem, setBulkEditItem] = useState({});
    
    // Find the fieldGroup matching the name attribute (memoized at top level)
    const matchingSection = useMemo(() => {
      if(loopAttribs!=='' && loopAttribs==='custom' && attribsName){
        return fieldGroups.find((section) => 
          (section.access_mode === undefined || section.access_mode !== 'HIDDEN') && 
          section.title.toLowerCase() === attribsName.toLowerCase()
        );
      }
      return null;
    }, [loopAttribs, attribsName, fieldGroups]);
    
    // Get subform data from defProps
    const { subform_data } = defProps || {};
    
    // Find subform index for this section (memoized)
    const subformIndex = useMemo(() => {
      if(matchingSection){
        return getSubformIndex(subform_data, matchingSection.id);
      }
      return -1;
    }, [subform_data, matchingSection]);
    
    // Memoize current subform data
    const currentSubformData = useMemo(() => {
      if (subformIndex >= 0 && subform_data && subform_data[subformIndex]) {
        return subform_data[subformIndex];
      }
      return { subform_rows: [] };
    }, [subformIndex, subform_data]);
    
    // Get section data (filtered like in SubformTypeForm) - memoized
    const sectionData = useMemo(() => {
      if(matchingSection){
        return getSectionData(matchingSection, modType);
      }
      return [];
    }, [matchingSection, modType]);
    
    // Calculate selected rows count for bulk actions - memoized
    const selectedRowsCount = useMemo(() => {
      return currentSubformData.subform_rows.filter((item) => item.is_selected === true).length;
    }, [currentSubformData.subform_rows]);
    
    const canShowBulkActions = selectedRowsCount > 1;
    const pageType = 'form'; // Default to form
    const isWrite = true; // Default to write mode
    
    // Bulk edit handlers (memoized at top level)
    const closeBulkEditPopup = useCallback((val = false) => {
      setBulkEditPopupOpen(val);
      setBulkEditPayload({
        type: "",
        value: ""
      });
      setBulkEditItem({});
    }, []);
    
    const updateBulkEditPayload = useCallback((updatedval) => {
      if(updatedval.type !== ""){
        let data = sectionData.find((item) => item.id === updatedval.type);
        setBulkEditItem(data || {});
        setBulkEditPayload(updatedval);
      } else {
        setBulkEditItem({});
        setBulkEditPayload(updatedval);
      }
    }, [sectionData]);
    
    // Add handler - adds a new row to the subform (memoized)
    const addHandler = useCallback(() => {
      if(matchingSection){
        const handler = createAddRowHandler({
          section: matchingSection,
          subformData: subform_data,
          subformIndex,
          modType,
          setSubFormDataAction
        });
        return handler();
      }
    }, [matchingSection, subform_data, subformIndex, modType, setSubFormDataAction]);
    
    // Bulk delete handler (memoized)
    const bulkDeleteHandler = useCallback(async () => {
      if(matchingSection){
        const handler = createBulkDeleteHandler({
          subformData: subform_data,
          subformIndex,
          currentSectionSubformData: currentSubformData,
          modType,
          setSubFormDataAction,
          clearSubformValidation,
          addRowHandler: addHandler
        });
        return handler();
      }
    }, [matchingSection, subform_data, subformIndex, currentSubformData, modType, setSubFormDataAction, clearSubformValidation, addHandler]);
    
    // Bulk edit change handler (memoized)
    const bulkEditChangeHandler = useCallback((updateData, e, inputType = 'text') => {
      const handler = createBulkEditChangeHandler(
        setBulkEditItem,
        setBulkEditPayload,
        bulkEditPayload
      );
      return handler(updateData, e, inputType);
    }, [setBulkEditItem, setBulkEditPayload, bulkEditPayload]);
    
    // Bulk edit save handler (memoized)
    const bulkEditSaveHandler = useCallback(() => {
      if(matchingSection){
        // Get latest subform_data from defProps when handler is called
        const latestSubformData = defProps?.subform_data || subform_data;
        if (!latestSubformData || latestSubformData[subformIndex] === undefined) return;
        
        const handler = createBulkEditSaveHandler({
          subformData: latestSubformData,
          subformIndex,
          bulkEditPayload,
          modType,
          setSubFormDataAction,
          fieldGroups,
          defProps,
          closeBulkEditPopup
        });
        return handler();
      }
    }, [matchingSection, defProps, subform_data, subformIndex, bulkEditPayload, modType, setSubFormDataAction, fieldGroups, closeBulkEditPopup]);
    
    // Bulk edit handler - opens the modal (memoized)
    const bulkEditHandler = useCallback(() => {
      if(matchingSection){
        // Get latest subform_data from defProps when handler is called
        const latestSubformData = defProps?.subform_data || subform_data;
        const latestSubformIndex = getSubformIndex(latestSubformData, matchingSection.id);
        const latestCurrentSubformData = (latestSubformIndex >= 0 && latestSubformData && latestSubformData[latestSubformIndex]) 
          ? latestSubformData[latestSubformIndex] 
          : { subform_rows: [] };
        
        const selectedRows = latestCurrentSubformData.subform_rows.filter((item) => item.is_selected === true);
        if (selectedRows.length > 1) {
          setBulkEditPopupOpen(true);
        }
      }
    }, [matchingSection, defProps, subform_data]);
    
    let element = null;
    if(loopAttribs!=='' && loopAttribs==='custom'){
      // Update dynamicVar with the found fieldGroup
      let updatedDynamicVar = {...dynamicVar};
      if(matchingSection && attribsName){
        // Create fields object from section.data array
        const fields = {};
        if(matchingSection.data && Array.isArray(matchingSection.data)){
          matchingSection.data.forEach((field) => {
            const fieldLabelName = field.label?.blocks?.[0]?.text || '';
            if(fieldLabelName){
              fields[fieldLabelName] = {...field,is_subform_field:true};
            }
          });
        }
        
        // Add fields object to matchingSection
        const sectionWithFields = {
          ...matchingSection,
          fields: fields
        };
        
        // Initialize actions structure for section-level buttons (Add, BulkEdit, BulkDelete)
        if (!updatedDynamicVar[attribsName]) {
          updatedDynamicVar[attribsName] = {};
        }
        
      updatedDynamicVar[attribsName] = {
          ...sectionWithFields,
          actions: {
            ...(updatedDynamicVar[attribsName].actions || {}),
            add: {
              onClick: addHandler,
              type: "add",
              access_mode: pageType === "form" && isWrite === true
            },
            edit: {
              onClick: bulkEditHandler,
              type: "BulkEdit",
              access_mode: canShowBulkActions
            },
            delete: {
              onClick: bulkDeleteHandler,
              type: "BulkDelete",
              access_mode: canShowBulkActions
            }
          },
          bulkEditState: {
            popupOpen: bulkEditPopupOpen,
            payload: bulkEditPayload,
            item: bulkEditItem
          },
          bulkEditHandlers: {
            closePopup: closeBulkEditPopup,
            updatePayload: updateBulkEditPayload,
            changeHandler: bulkEditChangeHandler,
            saveHandler: bulkEditSaveHandler
          }
        };
        
        // Store handlers and sectionData for use in return statement
        updatedDynamicVar[attribsName]._bulkEditHandlers = {
          closeBulkEditPopup,
          bulkEditChangeHandler,
          bulkEditSaveHandler,
          updateBulkEditPayload,
          sectionData
        };
      }
      if(children && children.length > 0){
        // Get handlers from dynamicVar if available
        const bulkEditHandlers = updatedDynamicVar[attribsName]?._bulkEditHandlers;
        return (
          <>
            {bulkEditPopupOpen && bulkEditHandlers && (
              <GroupEditForm
                closePopupToogle={bulkEditHandlers.closeBulkEditPopup}
                item={bulkEditItem}
                changeHandler={bulkEditHandlers.bulkEditChangeHandler}
                saveHandler={bulkEditHandlers.bulkEditSaveHandler}
                Fields={bulkEditHandlers.sectionData}
                updatePayload={bulkEditHandlers.updateBulkEditPayload}
                payload={bulkEditPayload}
                reminderDisplayText={defProps.reminderDisplayText}
                {...defProps}
              />
            )}
            {convertAndRenderElement(children,{...elmtParams,loopData,dataTable,dynamicVar:updatedDynamicVar,childActionButtons})}
          </>
        );
      }
    }

    fieldGroups.forEach((section,index) => {
      const FormTypeComp =  (section.type==='subform')?SubformTypeForm:SectionTypeForm; 
      if((section.access_mode==undefined || section.access_mode!=='HIDDEN') && (section.title.toLowerCase()===attribsName.toLowerCase())){
        element = <Wrp key={index}>
                  <FormTypeComp 
                    {...defProps}
                    section={JSON.stringify(section)}
                    column_type={section.column_type}
                    sectionIndex={index}
                    modType={modType}
                    isPopupWindow={false}
                    pageType={'form'} />
                    </Wrp>
      }
    })
  return element;  
    
}

ScmInputSectionField.defaultProps = {
    attributes:{},
    elmtParams:{}
}

const mapDispatchToProps = (dispatch) => {
    return {
        setSubFormDataAction: (modType, data) => dispatch(setSubFormDataAction(modType, data)),
        clearSubformValidation: (modType, row) => dispatch(clearSubformValidation(modType, row)),
    };
};

export default connect(null, mapDispatchToProps)(ScmInputSectionField);